/**
 * Provisioning.
 *
 * **Partly inferred — and prior discovery was wrong about this one, in a way worth stating
 * plainly.** The standing conclusion was "provisioning was never in Qualtrics; it is a branch of
 * the Airtable Install & Pre-Flight form." Half of that is now falsified: the owner-supplied
 * `Installation_Form_FIXED.qsf` contains a nine-question `Provisioning` block
 * (`BL_41QZjHqLeLwNhQy`), and its Survey Flow branches into that block *alone* when `Q1` is
 * answered `Provisioning`. So a Qualtrics provisioning path does exist and always has — it is a
 * branch of the install survey rather than a survey of its own, which is exactly why a survey-name
 * sweep did not find it.
 *
 * What that gives and does not give:
 *
 *  - **Sourced (9 questions):** `Q57`, `Q58`, `Q59`, `Q102`, `Q60`, `Q62`, `Q66`, `Q63`, `Q56`,
 *    verbatim from that block, with its real display logic including the two clauses gated on
 *    generation 4.0.
 *  - **Inferred (7 questions):** the ODIN diagnostics — attempt count, phone model, battery
 *    handling — plus the slots-online close-out. These come from the Airtable Install & Pre-Flight
 *    form as described in `discovery/airtable/21-INSTALL-PREFLIGHT-SCHEMA.md` and from the
 *    existence of matching `Work_Submission__c` fields
 *    (`Times_attempted_to_provisioning__c`, `Model_phone_used_to_provision__c`,
 *    `Provisioned_on_battery__c`). A field existing is decent evidence something asks for it, but
 *    it is not the question's wording, so the wording here is mine.
 *
 * Why a separate schema rather than reusing `install`: a provisioning-only visit is the case where
 * another crew did the install and this technician is only there to bring the unit online. Running
 * them through a schema whose every section is negated by `intervention_type = Provisioning` means
 * their form is defined by what it is not. This is the same question set, addressed directly.
 */

import type { FormSchema } from '../types.ts';

const WS = 'Work_Submission__c';
const DESCRIBE = 'discovery/salesforce/field-dumps.txt (Work_Submission__c describe)';

function maps(field: string, type: string, caveat?: string) {
  return {
    object: WS,
    field,
    type,
    confidence: 'verified' as const,
    evidence: DESCRIBE,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

function guess(field: string, why: string) {
  return { object: WS, field, confidence: 'unverified' as const, evidence: `No describe hit. ${why}` };
}

const NOT_PROVISIONED = { field: 'provisioned', op: 'eq' as const, value: false };
const IS_ODIN = { field: 'ctx.lockerPlatform', op: 'eq' as const, value: 'ODIN' };

export const PROVISIONING: FormSchema = {
  id: 'provisioning',
  version: 1,
  title: 'Provisioning',
  source: {
    provenance: 'partly-inferred',
    artefact:
      'Installation_Form_FIXED.qsf, block BL_41QZjHqLeLwNhQy "Provisioning" (9 questions, sourced) + discovery/airtable/21-INSTALL-PREFLIGHT-SCHEMA.md and the Work_Submission__c provisioning fields (7 questions, inferred)',
    sourceQuestionCount: 9,
    note:
      'The nine sourced questions carry their Qualtrics DataExportTag. Every inferred field is flagged with `inferred: true` and says what suggested it. Prior discovery recorded that provisioning was absent from Qualtrics; that is corrected here — it is a branch of the install survey, reachable on its own via Q1.',
  },
  destination: {
    object: 'Work_Submission__c',
    recordTypeLabel: 'Provisioning',
    confidence: 'verified',
    evidence:
      'Work_Submission__c carries an active Provisioning record type (discovery/salesforce/field-dumps.txt record type list). The record type is verified. No Qualtrics workflow writes it today — the install survey has no captured mapping at all — so the per-field mapping below is name-matching against the describe, not an observed integration.',
  },
  sections: [
    {
      id: 'visit',
      title: 'This visit',
      subtitle: 'All from the record. Correct anything wrong.',
      fields: [
        {
          id: 'kiosk_id',
          kind: 'text',
          label: 'Kiosk ID',
          required: true,
          prefill: { source: 'kiosk.id' },
          maps: maps('Kiosk_ID__c', 'string(255)'),
        },
        {
          id: 'locker_name',
          kind: 'text',
          label: 'Locker name',
          required: true,
          prefill: { source: 'kiosk.name' },
          maps: maps('Locker_Name__c', 'string(255)'),
        },
        {
          id: 'generation',
          kind: 'single',
          label: 'Locker generation',
          options: ['2.5', '3.0', '4.0', '5.0'],
          required: true,
          prefill: { source: 'kiosk.generation' },
          maps: maps('Locker_Generation__c', 'picklist 2|2.5|3|4|5', 'Value sets disagree across objects; 3.0 will not match 3.'),
        },
        {
          id: 'layout',
          kind: 'text',
          label: 'Locker layout',
          required: true,
          prefill: { source: 'kiosk.layout' },
          maps: maps('Locker_Layout__c', 'picklist (15 values)', 'Restricted picklist against a free-text concept. Needs a normalisation map.'),
        },
        {
          id: 'ticket_id',
          kind: 'text',
          label: 'Post Install ID (PIID)',
          prefill: { source: 'intervention.postInstallId' },
          maps: maps('Ticket_ID__c', 'string(255)', 'Post_Install__c is the reference field, but the write allowlist refuses it: infra/src/writer/field-policy.ts lists it under PLATFORM_CORRECTED_FIELDS after UAT proved the platform overwrites it. Ticket_ID__c is the writable carrier; a before-insert handler derives Post_Install__c from it.'),
        },
        {
          id: 'visit_start',
          kind: 'time',
          label: 'Visit start time (HH:MM, 24-hour)',
          required: true,
          prefill: { source: 'visit.startTime' },
          validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
          maps: maps('Visit_Start_Time__c', 'string(30)'),
        },
      ],
    },
    {
      id: 'code',
      title: 'Provisioning code',
      fields: [
        {
          id: 'provisioning_code',
          kind: 'text',
          label: 'Provisioning code',
          help: 'Already on the intervention for 14% of visits. Never logged and never sent to analytics.',
          required: true,
          prefill: { source: 'intervention.provisioningCode', overrideHint: 'Type it in if the record does not have it.' },
          inferred: true,
          maps: maps('Provisioning_Code__c', 'string(255)'),
        },
        {
          id: 'code_note',
          kind: 'note',
          label:
            'If the record supplied this code, it is shown to you rather than asked. That is the difference between a nine-tap form and a form you have to look something up for.',
          showIf: { field: 'ctx.hasProvisioningCode', op: 'eq', value: true },
        },
      ],
    },
    {
      id: 'outcome',
      title: 'Did it provision?',
      fields: [
        {
          id: 'provisioned',
          kind: 'yesno',
          label: 'Has the locker been provisioned?',
          required: true,
          sourceTag: 'Q57',
          maps: maps('Is_the_locker_provisioned__c', 'picklist Yes|No'),
        },
        {
          id: 'provisioning_photo',
          kind: 'photo',
          label: 'Photo of the provisioning',
          help: 'The screen showing the provisioned state. If this is an ODIN locker and it failed, screenshot the phone instead.',
          required: true,
          showIf: { field: 'provisioned', op: 'eq', value: true },
          validate: { minPhotos: 1, maxPhotos: 2 },
          sourceTag: 'Q58',
          maps: maps('Provisioning_Screen_Picture__c', 'textarea(5000)'),
        },
        {
          id: 'failure_reason',
          kind: 'single',
          label: 'If not provisioned, why?',
          options: [
            'Amazon Related - System unavailable error',
            'Amazon Related - Credentials failed',
            'Amazon Related - Re-Image Needed',
            'Host Related - Data ports not setup',
            'Host Related - Firewall',
            'Host Related - Network not detected',
            'Host Related - No power/final connection not completed',
            'Host Related - Location Not Accessible',
            'Supplier Related - Problem with setup error',
            'Supplier Related - Improper tools/equipment on site',
            'Supplier Related - Abandoned',
            'Supplier Related - Technician Error',
            'Hardware - Control Board',
            'Hardware - Screen',
            'Hardware - IPC',
            'Hardware - Modem/Router',
            'Hardware - Antennas',
            'Hardware - Other',
            'Force Majeure',
          ],
          required: true,
          showIf: NOT_PROVISIONED,
          sourceTag: 'Q59',
          maps: maps('Provisioning_failure_reason__c', 'picklist (24 values)', 'Survey wording (19 values) differs from the field wording (24 values, "Hardware / Parts - Screen" rather than "Hardware - Screen"). Survey wording kept because it is what technicians read; a normalisation map is required before this writes.'),
        },
        {
          id: 'failure_hardware_other',
          kind: 'text',
          label: 'What other hardware / part was the issue?',
          required: true,
          showIf: { field: 'failure_reason', op: 'eq', value: 'Hardware - Other' },
          sourceTag: 'Q102',
          maps: maps('Parts_preventing_provisioning__c', 'string(255)'),
        },
        {
          id: 'failure_detail',
          kind: 'textarea',
          label: 'What happened, in your words?',
          required: true,
          showIf: NOT_PROVISIONED,
          inferred: true,
          validate: { maxLength: 2000 },
          maps: maps('If_no_describe_the_issue__c', 'textarea(2000)', 'Field label on the object is "Why has the locker not been provisioned".'),
        },
        {
          id: 'failure_photo',
          kind: 'photo',
          label: 'Photo of the error',
          help: 'The screen, or a screenshot of the phone for ODIN.',
          required: true,
          showIf: NOT_PROVISIONED,
          inferred: true,
          validate: { minPhotos: 1, maxPhotos: 3 },
          maps: maps('Additional_Photos_if_needed__c', 'textarea(5000)'),
        },
      ],
    },
    {
      id: 'escalation',
      title: 'Escalation',
      showIf: NOT_PROVISIONED,
      fields: [
        {
          id: 'spoke_to_ots',
          kind: 'yesno',
          label: 'Have you spoken to OTS?',
          required: true,
          sourceTag: 'Q60',
          maps: guess('Spoke_To_OTS__c', 'The Maintenance path has Did_You_try_to_contact_with_OTS_suppor__c; no provisioning-side equivalent found.'),
        },
        {
          id: 'ots_instruction',
          kind: 'textarea',
          label: 'What did OTS tell you to do?',
          required: true,
          showIf: { field: 'spoke_to_ots', op: 'eq', value: true },
          inferred: true,
          validate: { maxLength: 3000 },
          maps: maps('Additional_Comments_if_needed__c', 'textarea(20000)'),
        },
        {
          id: 'extra_parts_needed',
          kind: 'yesno',
          label: 'Were additional parts needed to complete provisioning?',
          required: true,
          sourceTag: 'Q62',
          maps: maps('additional_parts_needed_to_provision__c', 'picklist Yes|No'),
        },
        {
          id: 'extra_parts_which',
          kind: 'textarea',
          label: 'Which parts are needed?',
          required: true,
          showIf: { field: 'extra_parts_needed', op: 'eq', value: true },
          inferred: true,
          validate: { maxLength: 20000 },
          maps: maps('Parts_needed_to_provision__c', 'textarea(20000)'),
        },
      ],
    },
    {
      id: 'odin',
      title: 'ODIN diagnostics',
      subtitle:
        'Shown only for ODIN hardware, which is derived from Access_Point_Type__c rather than asked. These are how a provisioning-app problem gets diagnosed without sending someone back.',
      showIf: IS_ODIN,
      fields: [
        {
          id: 'phone_connected',
          kind: 'yesno',
          label: 'Did your phone connect to the locker?',
          required: true,
          showIf: NOT_PROVISIONED,
          sourceTag: 'Q66',
          maps: guess('Phone_Connected_To_Locker__c', 'No matching field found in the describe.'),
        },
        {
          id: 'odin_app_version',
          kind: 'text',
          label: 'What version of the ODIN provisioning app are you using?',
          required: true,
          showIf: NOT_PROVISIONED,
          sourceTag: 'Q63',
          maps: maps('Version_of_Odin_provisioning_app_used__c', 'string(255)'),
        },
        {
          id: 'odin_attempts',
          kind: 'number',
          label: 'How many provisioning attempts did it take?',
          required: true,
          inferred: true,
          validate: { integer: true, min: 1, max: 99 },
          maps: maps('Times_attempted_to_provisioning__c', 'string(255)', 'Stored as text on the object despite being a count. Sent as a decimal string.'),
        },
        {
          id: 'odin_phone_model',
          kind: 'text',
          label: 'What model phone were you using?',
          required: true,
          inferred: true,
          validate: { maxLength: 255 },
          maps: maps('Model_phone_used_to_provision__c', 'string(255)'),
        },
        {
          id: 'odin_batteries',
          kind: 'yesno',
          label: 'Did you turn on the batteries as soon as you unloaded the locker?',
          required: true,
          inferred: true,
          maps: maps('Provisioned_on_battery__c', 'boolean, required on the object', 'Required with no default on Work_Submission__c, so every provisioning write must supply it. That is why this question is required here rather than optional.'),
        },
      ],
    },
    {
      id: 'close',
      title: 'Close out',
      fields: [
        {
          id: 'screen_illuminated',
          kind: 'yesno',
          label: 'Is the locker screen illuminated?',
          required: true,
          maps: maps('Is_the_locker_screen_illuminated__c', 'picklist', 'Verified through the Maintenance workflow mapping in discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md.'),
        },
        {
          id: 'screen_issue',
          kind: 'textarea',
          label: 'Describe the issue with the screen',
          required: true,
          showIf: { field: 'screen_illuminated', op: 'eq', value: false },
          inferred: true,
          validate: { maxLength: 2000 },
          maps: maps('What_were_the_issues__c', 'textarea'),
        },
        {
          id: 'slots_online',
          kind: 'yesno',
          label: 'Are all slots online?',
          required: true,
          showIf: { field: 'provisioned', op: 'eq', value: true },
          inferred: true,
          maps: maps('Are_all_slots_online__c', 'picklist', 'Verified through the Maintenance workflow mapping.'),
        },
        {
          id: 'slots_pending',
          kind: 'textarea',
          label: 'What is pending for all slots to be online?',
          required: true,
          showIf: { field: 'slots_online', op: 'eq', value: false },
          inferred: true,
          validate: { maxLength: 3000 },
          maps: maps('Slots_offline_reason__c', 'textarea', 'Verified through the Maintenance workflow mapping.'),
        },
        {
          id: 'comments',
          kind: 'textarea',
          label: 'Anything else the next person needs to know?',
          validate: { maxLength: 3000 },
          maps: maps('Additional_Comments__c', 'textarea(3000)', 'Describe says 3000; infra declares 32768. Stricter limit enforced here.'),
        },
        {
          id: 'visit_end',
          kind: 'time',
          label: 'Visit end time (HH:MM, 24-hour)',
          required: true,
          validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
          sourceTag: 'Q56',
          maps: maps('Visit_End_Time__c', 'string(30)'),
        },
      ],
    },
  ],
};
